import { PLAYFIELD } from '../core/Constants';

/**
 * Energy Ball entity with sub-stepping and anti-stall physics
 */
export class Ball {
  constructor(x, y, radius = 5, speed = 5.6) {
    this.x = x;
    this.y = y;
    this.oldX = x;
    this.oldY = y;
    this.radius = radius;
    this.baseSpeed = speed;
    this.speed = speed;
    this.maxSpeed = 11.5;
    this.speedIncrement = 0.08;

    this.vx = 0;
    this.vy = 0;
    this.isStuck = true; // Attached to paddle initially or on catch
    this.stuckOffsetX = 0; // Relative to paddle center

    // Previous positions for fragment shader motion blur / trail
    this.trail = [
      { x, y },
      { x, y },
      { x, y },
    ];
  }

  attachToPaddle(paddle, offsetX = 0) {
    this.isStuck = true;
    this.stuckOffsetX = offsetX;
    this.updateStuck(paddle);
    this.vx = 0;
    this.vy = 0;
  }

  updateStuck(paddle) {
    this.x = paddle.x + paddle.width / 2 + this.stuckOffsetX;
    this.y = paddle.y - this.radius - 1;
    const minX = paddle.x + this.radius;
    const maxX = paddle.x + paddle.width - this.radius;
    this.x = Math.max(minX, Math.min(maxX, this.x));
  }

  launch(angleRad = -Math.PI / 3) {
    if (!this.isStuck) return;
    this.isStuck = false;
    this.vx = Math.sin(angleRad) * this.speed;
    this.vy = -Math.abs(Math.cos(angleRad) * this.speed);
  }

  clone(speedMultiplier = 1.0) {
    const cloneBall = new Ball(this.x, this.y, this.radius, this.speed * speedMultiplier);
    cloneBall.isStuck = false;
    const currentAngle = Math.atan2(this.vy, this.vx);
    const newAngle = currentAngle + (Math.random() * 0.6 - 0.3);
    cloneBall.vx = Math.cos(newAngle) * cloneBall.speed;
    cloneBall.vy = Math.sin(newAngle) * cloneBall.speed;
    if (Math.abs(cloneBall.vy) < 1.6) {
      cloneBall.vy = cloneBall.vy < 0 ? -2.0 : 2.0;
    }
    return cloneBall;
  }

  slowDown() {
    this.speed = Math.max(this.baseSpeed, this.speed * 0.75);
    const angle = Math.atan2(this.vy, this.vx);
    this.vx = Math.cos(angle) * this.speed;
    this.vy = Math.sin(angle) * this.speed;
  }

  recordTrail() {
    this.trail.pop();
    this.trail.unshift({ x: this.x, y: this.y });
  }

  stepMove(subDt, paddle) {
    let hitBoundary = null;

    this.x += this.vx * subDt * 60;
    this.y += this.vy * subDt * 60;

    // Solid Black Unbreakable Left Wall Collision
    if (this.x - this.radius <= PLAYFIELD.left) {
      this.x = PLAYFIELD.left + this.radius;
      this.vx = Math.abs(this.vx);
      hitBoundary = 'WALL';
    }
    // Solid Black Unbreakable Right Wall Collision
    if (this.x + this.radius >= PLAYFIELD.right) {
      this.x = PLAYFIELD.right - this.radius;
      this.vx = -Math.abs(this.vx);
      hitBoundary = 'WALL';
    }
    // Top boundary
    if (this.y - this.radius <= PLAYFIELD.top) {
      this.y = PLAYFIELD.top + this.radius;
      this.vy = Math.abs(this.vy);
      hitBoundary = 'TOP';
    }

    // Anti-stall: never allow ball to be completely horizontal
    if (Math.abs(this.vy) < 1.2 && !this.isStuck) {
      this.vy = this.vy < 0 ? -1.8 : 1.8;
    }

    return hitBoundary;
  }

  bounceFromPaddle(paddle, offsetFactor) {
    const maxBounceAngle = (75 * Math.PI) / 180;
    const bounceAngle = offsetFactor * maxBounceAngle;

    this.speed = Math.min(this.maxSpeed, this.speed + this.speedIncrement);
    this.vx = Math.sin(bounceAngle) * this.speed;
    this.vy = -Math.cos(bounceAngle) * this.speed;

    if (Math.abs(this.vy) < 1.6) {
      this.vy = -2.0;
    }

    this.y = paddle.y - this.radius - 1;
  }

  isDead() {
    return this.y - this.radius > PLAYFIELD.bottom;
  }

  getBounds() {
    return {
      left: this.x - this.radius,
      right: this.x + this.radius,
      top: this.y - this.radius,
      bottom: this.y + this.radius,
    };
  }
}
